import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/features/auth/services/authService';
import { clearRememberMe } from '@/features/auth/utils/rememberMeStorage';
import { apiService, KIOSK_AUTH_TOKEN_CHANGED_EVENT } from '@/services/apiService';
import { useSurface } from '@/contexts/SurfaceContext';
import { notificationService } from '@/services/notificationService';
import i18n from '@/i18n';
import { isAutoLogoutHeld } from '@/utils/autoLogoutHold';
import { KIOSK_HOST_ACTIVITY_EVENT } from '@/platform/kioskHost';

interface AutoLogoutConfig {
  /** Idle time before the warning and its countdown appear. */
  idleMs: number;
  /** Length of the countdown; the Player is signed out when it reaches zero. */
  countdownMs: number;
}

/** 10 seconds without touching the screen, then a 20-second countdown, then sign-out. */
const IDLE_CONFIG: AutoLogoutConfig = { idleMs: 10_000, countdownMs: 20_000 };

// Kiosk devices are shared, so an abandoned session is closed quickly (the `idleSignOut`
// capability, src/config/surfacePolicy.ts). Non-kiosk: no idle timeout (the session lasts until
// the token expires).

// Local dev hostnames: while the surface is still unknown they behave like a kiosk, so the flow can
// be tried; a known surface (VITE_DEV_DOMAIN_TYPE, the demo's ?surface=) decides as anywhere else.
const DEV_HOSTS = new Set(['localhost', '127.0.0.1']);

/**
 * Idle allowance while a screen holds the timer (payment gateway in an iframe, QR being scanned
 * with a phone): the Player is busy where the app cannot see input, so they get minutes, not
 * seconds — but a session abandoned there is still closed.
 */
const HELD_IDLE_MS = 5 * 60_000;

// KIOSK_HOST_ACTIVITY_EVENT: a tap in a kiosk shell window (a payment page) the app cannot see.
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'keypress',
  'wheel',
  KIOSK_HOST_ACTIVITY_EVENT,
];
const TICK_MS = 250;

export interface AutoLogoutState {
  /** Whole seconds left on the countdown (0 while the warning is hidden). */
  secondsRemaining: number;
  /** Full length of the countdown, for a progress bar. */
  countdownSeconds: number;
  isWarningVisible: boolean;
  /** "I'm still here": same as touching the screen. */
  resetTimer: () => void;
}

/**
 * Signs a Player out of an abandoned screen.
 *
 * Active only while someone is signed in, where the surface can('idleSignOut') (kiosks). After
 * `idleMs` without input a warning counts down `countdownMs`; any input, or "Stay signed in",
 * starts over. Screens that hold the timer (payment in progress) get a longer idle allowance.
 */
export function useAutoLogout(): AutoLogoutState {
  const navigate = useNavigate();
  const lastActivityRef = useRef<number>(Date.now());
  const warningVisibleRef = useRef(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [isWarningVisible, setIsWarningVisible] = useState(false);

  const { surface, can } = useSurface();
  const devHostKiosk = surface === 'unknown' && DEV_HOSTS.has(globalThis.location?.hostname ?? '');
  const config: AutoLogoutConfig | null = can('idleSignOut') || devHostKiosk ? IDLE_CONFIG : null;
  const [signedIn, setSignedIn] = useState(() => apiService.isAuthenticated());

  // Only a signed-in session can be signed out
  useEffect(() => {
    const sync = () => setSignedIn(apiService.isAuthenticated());
    window.addEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const hideWarning = useCallback(() => {
    if (!warningVisibleRef.current) return;
    warningVisibleRef.current = false;
    setIsWarningVisible(false);
    setSecondsRemaining(0);
  }, []);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    hideWarning();
  }, [hideWarning]);

  const active = config !== null && signedIn;
  const idleMs = active ? config.idleMs : 0;
  const countdownMs = active ? config.countdownMs : 0;

  useEffect(() => {
    if (!active) {
      hideWarning();
      return;
    }

    let stopped = false;
    lastActivityRef.current = Date.now();

    const signOut = async () => {
      if (stopped) return;
      stopped = true;
      clearInterval(interval);
      hideWarning();
      try {
        await authService.logout();
      } catch {
        /* the local session is cleared below either way */
      }
      apiService.clearTokens();
      apiService.clearUserCache();
      clearRememberMe();
      notificationService.info(
        i18n.t('autoLogout.signedOut', 'You were signed out because the screen was idle.'),
        { duration: 8000 }
      );
      navigate('/', { replace: true });
    };

    const tick = () => {
      if (stopped) return;
      // A payment on screen gets a much longer idle allowance before the countdown starts.
      const allowedIdleMs = isAutoLogoutHeld() ? Math.max(idleMs, HELD_IDLE_MS) : idleMs;
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed < allowedIdleMs) {
        hideWarning();
        return;
      }
      const remainingMs = allowedIdleMs + countdownMs - elapsed;
      if (remainingMs <= 0) {
        void signOut();
        return;
      }
      warningVisibleRef.current = true;
      setIsWarningVisible(true);
      setSecondsRemaining(Math.ceil(remainingMs / 1000));
    };

    const interval = setInterval(tick, TICK_MS);
    const onActivity = () => {
      if (!stopped) resetTimer();
    };
    ACTIVITY_EVENTS.forEach((event) => globalThis.addEventListener(event, onActivity, { passive: true }));

    return () => {
      stopped = true;
      clearInterval(interval);
      ACTIVITY_EVENTS.forEach((event) => globalThis.removeEventListener(event, onActivity));
      hideWarning();
    };
  }, [active, idleMs, countdownMs, navigate, resetTimer, hideWarning]);

  return {
    secondsRemaining,
    countdownSeconds: Math.round(countdownMs / 1000),
    isWarningVisible,
    resetTimer,
  };
}
